// ─── Bandeau permanent : « où suis-je en train de travailler » ───────────
//
// CE QU'IL RÉPARE, arrivé pour de vrai le 2026-08-08. Le cockpit staging et
// le cockpit production sont identiques à l'écran, même charte, mêmes blocs.
// Un vrai rapport de laboratoire a été déposé en production en croyant être
// sur staging. Sans conséquence cette fois — mais la seule raison en est la
// nature du geste, pas une protection.
//
// L'ancien bandeau existait déjà. Il faisait 18 pixels de haut, en police de
// 10 : il était là, et personne ne l'a vu. Un avertissement qu'on ne voit pas
// ne vaut pas mieux qu'un avertissement absent.
//
// ⚠ TROIS ÉTATS, PAS DEUX. Le cas dangereux n'est pas « staging ou
// production », c'est « je ne sais pas ». Une variable absente affiche donc un
// avertissement explicite, jamais rien. Se taire serait reproduire exactement
// la situation qui a causé l'erreur.
//
// ⚠ CE N'EST QU'UN RAPPEL. Le verrou réel des sorties se pilote sur `APP_ENV`
// côté serveur — voir `_mgd-security.js` — et ne dépend en rien de ce
// composant. Un bandeau informe, il ne protège pas.
//
// `pointer-events: none` : il ne doit jamais intercepter un clic. Un
// avertissement qui gêne finit par être retiré.

import { useEffect } from 'react';

/**
 * L'environnement affiché, à partir de la variable de build.
 *
 * Fonction pure : c'est elle que les tests exercent. Toute valeur inconnue,
 * absente ou mal orthographiée retombe sur `inconnu` — jamais sur une
 * supposition.
 */
export function environnementAffiche(valeur) {
  const brut = String(valeur ?? '').trim().toLowerCase();
  if (brut === 'staging') return 'staging';
  if (brut === 'production') return 'production';
  return 'inconnu';
}

/** Le rendu de chaque état. Le plus visible est le plus dangereux. */
export const BANDEAUX = Object.freeze({
  staging: {
    texte: 'STAGING — DONNÉES DE TEST · AUCUN ENVOI CLIENTE',
    hauteur: 30,
    police: 13,
    couleur: '#3a2a00',
    fond: 'repeating-linear-gradient(135deg,#f5c542 0 14px,#e8b62c 14px 28px)',
  },
  inconnu: {
    texte: 'ENVIRONNEMENT NON IDENTIFIÉ — VÉRIFIE AVANT TOUTE ACTION',
    hauteur: 30,
    police: 13,
    couleur: '#fff',
    fond: 'repeating-linear-gradient(135deg,#a53b32 0 14px,#8d2f28 14px 28px)',
  },
  // ⚠ 25.08.2026 — LA PRODUCTION NE S'AFFICHE PLUS.
  //
  // Ce bandeau est ne le 08.08 d'un incident reel : un vrai rapport de
  // laboratoire depose en production en croyant etre sur staging. Deux
  // cockpits identiques a l'ecran, aucune marque pour les distinguer.
  //
  // Les DEUX stagings ont ete supprimes le 22.08. Il n'existe plus qu'un
  // seul cockpit, donc « ou suis-je » n'a plus qu'une reponse et la
  // rappeler chaque jour n'apprend plus rien. Un avertissement permanent
  // qui n'avertit de rien finit par ne plus etre lu du tout — et ce jour-la
  // il ne servira pas non plus pour les deux cas qui comptent encore.
  //
  // ⚠ NE PAS SUPPRIMER LE COMPOSANT. `staging` et `inconnu` restent
  // affiches : si un staging revient un jour, ou si la variable
  // d'environnement disparait, le garde-fou est deja en place. C'est
  // exactement le silence en production qui avait cause l'incident, mais
  // il ne le cause plus une fois qu'il n'y a plus d'ailleurs.
  production: null,
});

export const enStaging = environnementAffiche(import.meta.env.VITE_ENVIRONNEMENT) === 'staging';

export default function BandeauStaging({ environnement = import.meta.env.VITE_ENVIRONNEMENT }) {
  const etat = environnementAffiche(environnement);
  const bandeau = BANDEAUX[etat];

  // Le bandeau est fixe : sans compensation il recouvre l'en-tête, ce que
  // faisait déjà l'ancien. On rend au corps de page exactement la hauteur
  // qu'on lui prend, et on la lui rend en partant.
  //
  // ⚠ Le hook est appelé même quand il n'y a rien à afficher : les hooks
  // React ne se sautent pas. On rend alors zéro pixel, ce qui revient à ne
  // pas toucher à la page.
  const hauteur = bandeau?.hauteur ?? 0;
  useEffect(() => {
    const corps = globalThis.document?.body;
    if (!corps || !hauteur) return undefined;
    const precedent = corps.style.paddingTop;
    corps.style.paddingTop = `${hauteur}px`;
    return () => { corps.style.paddingTop = precedent; };
  }, [hauteur]);

  // Production : plus rien à dire, il n'existe qu'un seul cockpit.
  if (!bandeau) return null;

  return (
    <div
      role="status"
      data-environnement={etat}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        pointerEvents: 'none',
        height: bandeau.hauteur,
        lineHeight: `${bandeau.hauteur}px`,
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
        fontSize: bandeau.police,
        letterSpacing: '0.16em',
        fontWeight: 700,
        color: bandeau.couleur,
        background: bandeau.fond,
        boxShadow: '0 1px 3px rgba(0,0,0,.25)',
      }}
    >
      {bandeau.texte}
    </div>
  );
}
